import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './contextual-menu-horizontal-scrolling-section.module.scss';

interface ContextualMenuHorizontalScrollingSectionProps {
  className?: string;
  hideScrollbar?: boolean;
  children: ReactElement<any> | (ReactElement<any> | null)[] | null;
  showScrollIcons?: boolean;
  doSmoothAnimationOnScrollIconClick?: boolean;
}

export function ContextualMenuHorizontalScrollingSection({
  className = '',
  hideScrollbar = true,
  showScrollIcons = false,
  doSmoothAnimationOnScrollIconClick = false,
  ...props
}: ContextualMenuHorizontalScrollingSectionProps): ReactElement<any> | null {
  const [ref, setRefState] = useState<HTMLUListElement | null>(null);
  const [scrollLeftValue, setScrollLeftValue] = useState(0);
  const [scrollIntoView, setScrollIntoView] = useState(false);

  useEffect((): void => {
    if (ref && !scrollIntoView) {
      setScrollIntoView(true);
    }
  }, [ref]);

  useEffect(() => {
    if (ref && showScrollIcons) {
      ref.addEventListener('scroll', handleScroll);

      return (): void => {
        ref.removeEventListener('scroll', handleScroll);
      };
    }
    return (): void => {};
  }, [ref]);

  const handleScroll = (): void => {
    if (ref) {
      setScrollLeftValue(ref.scrollLeft);
    }
  };

  const showLeftScrollIcon = (): boolean => {
    return scrollLeftValue !== 0;
  };

  const showRightScrollIcon = (): boolean => {
    return !(ref && scrollLeftValue === ref.scrollWidth - ref.clientWidth);
  };

  const scrollLeft = (): void => {
    if (ref) {
      if (doSmoothAnimationOnScrollIconClick) {
        ref.scrollBy({
          left: -(ref.clientWidth - 60),
          behavior: 'smooth',
        });
      } else {
        ref.scrollLeft = ref.scrollLeft - ref.clientWidth + 60;
      }
    }
  };
  const scrollRight = (): void => {
    if (ref) {
      if (doSmoothAnimationOnScrollIconClick) {
        ref.scrollBy({
          left: ref.clientWidth - 60,
          behavior: 'smooth',
        });
      } else {
        ref.scrollLeft = ref.scrollLeft + ref.clientWidth - 60;
      }
    }
  };

  return (
    <>
      {showScrollIcons && showLeftScrollIcon() ? (
        <Icon onClick={scrollLeft} size={IconSize.SIZE_ICON_16} className={styles.iconLeft} icon="icon-caret-left" shape={IconShape.CIRCLE} type={IconType.SHADOW} />
      ) : null}
      <ul
        ref={(e): void => {
          setRefState(e);
        }}
        className={`${className} ${hideScrollbar ? styles.hiddenScrollbar : ''}`}
      >
        {props.children}
      </ul>
      {showScrollIcons && showRightScrollIcon() ? (
        <Icon onClick={scrollRight} size={IconSize.SIZE_ICON_16} className={styles.iconRight} icon="icon-caret-right" shape={IconShape.CIRCLE} type={IconType.SHADOW} />
      ) : null}
    </>
  );
}
